import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { BlockchainResult } from "@/lib/api";
import BlockchainPage from "@/pages/BlockchainPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const id = params.id!;
  const chain = await safeFetchJson<BlockchainResult>(
    `${API_BASE}/blockchain/${encodeURIComponent(id)}`,
    { timeoutMs: 3000 },
  );
  return { id, chain };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const id = data?.id ?? "";
  const chain = data?.chain;
  const origin = "https://loadlogo.com";

  if (chain && !chain.error) {
    const description = `${chain.name} blockchain logo. Chain ID: ${chain.chainId}.${chain.nativeCurrency ? ` Native currency: ${chain.nativeCurrency.symbol}.` : ""} Fetch chain logos via ${SITE_NAME}.`;
    return [
      { title: `${chain.name} Logo | Chain ${chain.chainId} | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${chain.name} Logo | Chain ${chain.chainId} | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/blockchain/${id}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/blockchain/${id}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${chain.name} Logo | Chain ${chain.chainId} | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/blockchain/${id}` },
      { tagName: "link", rel: "canonical", href: `${origin}/blockchain/${id}` },
    ];
  }

  return [
    { title: `Blockchain ${id} Logo | ${SITE_NAME}` },
    {
      name: "description",
      content: `Blockchain logo for chain ${id}. Fetch chain logos via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `Blockchain ${id} Logo | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `Blockchain logo for chain ${id}. Fetch chain logos via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/blockchain/${id}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/blockchain/${id}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/blockchain/${id}` },
  ];
};

export default function BlockchainRoute() {
  const { chain } = useLoaderData<typeof loader>();
  return <BlockchainPage serverData={chain} />;
}
